import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { CalendarDays, GraduationCap, Wallet, Activity, ArrowRightLeft, XCircle, UserMinus, Archive, ArchiveRestore, CheckCircle2, Loader2, ShieldAlert } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { toast } from 'sonner';
import { usePermV2 } from '../../../lib/perm_v2';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const STATUS_STYLE = {
  ativo: 'dna-tone-success',
  sem_turma: 'dna-tone-warning',
  cancelado: 'dna-tone-danger',
  transferido: 'dna-tone-info',
};

const Metric = ({ icon: Icon, label, value, sub }) => (
  <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-3">
    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-slate-400">
      <Icon className="h-3.5 w-3.5" /> {label}
    </div>
    <p className="mt-1 text-lg font-bold text-slate-800">{value}</p>
    {sub && <p className="text-[11px] text-slate-400">{sub}</p>}
  </div>
);

export default function VinculosAnuais({ studentId, onChanged, year: yearProp, onYearChange }) {
  const { financeiroVisivel } = usePermV2();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [yearState, setYearState] = useState(null);
  const yearRef = React.useRef(null);
  const year = yearProp !== undefined && yearProp !== null ? yearProp : yearState;
  yearRef.current = year;
  const setYear = useCallback((y) => {
    setYearState(y);
    if (onYearChange) onYearChange(y);
  }, [onYearChange]);
  const [classGroups, setClassGroups] = useState([]);
  const [action, setAction] = useState(null); // {kind, link}
  const [reason, setReason] = useState('');
  const [targetClass, setTargetClass] = useState('');
  const [busy, setBusy] = useState(false);

  const headers = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }), []);

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/enrollments/student/${studentId}`, { headers: headers() });
      const j = await res.json();
      if (!res.ok) throw new Error(j.detail || 'Erro');
      setData(j);
      const years = (j.years || []).map((y) => y.year);
      if (!years.includes(yearRef.current)) setYear(years[years.length - 1] ?? null);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [studentId, headers]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/class-groups`, { headers: headers() });
        const j = await res.json();
        setClassGroups(Array.isArray(j) ? j : (j.items || []));
      } catch (e) { /* noop */ }
    })();
  }, [headers]);

  const current = (data?.years || []).find((y) => y.year === year);

  const post = async (url, body) => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/enrollments${url}`, {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof j.detail === 'string' ? j.detail : 'Falha na operação');
      toast.success('Operação concluída');
      setAction(null); setReason(''); setTargetClass('');
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const confirmAction = () => {
    if (!action) return;
    const l = action.link;
    if (action.kind === 'remove') return post(`/${l.id}/remove-from-class`, { reason });
    if (action.kind === 'cancel') return post(`/${l.id}/cancel`, { reason });
    if (action.kind === 'transfer') {
      if (!targetClass) { toast.error('Selecione a turma destino'); return; }
      return post(`/${l.id}/transfer`, { new_class_group_id: targetClass, reason });
    }
    if (action.kind === 'archive') return post(`/student/${studentId}/archive`, { reason });
    return undefined;
  };

  if (!studentId) return null;

  const sameYearClasses = classGroups.filter((c) => Number(c.year) === Number(year));

  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm" data-testid="vinculos-anuais-card">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="grid h-8 w-8 place-items-center rounded-lg bg-indigo-50"><CalendarDays className="h-4 w-4 text-indigo-600" /></span>
          <div>
            <h3 className="text-base font-bold text-slate-800">Vínculos por ano letivo</h3>
            <p className="text-[11px] text-slate-400">Histórico preservado — rematrícula nunca sobrescreve o ano anterior</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {data?.archived && (
            <span className="rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-[11px] font-semibold text-rose-700" data-testid="vinculos-archived-badge">Arquivado</span>
          )}
          {data?.archived ? (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => post(`/student/${studentId}/unarchive`)} data-testid="vinculos-unarchive-btn">
              <ArchiveRestore className="mr-1.5 h-3.5 w-3.5" /> Desarquivar
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={() => { setAction({ kind: 'archive' }); setReason(''); }} data-testid="vinculos-archive-btn">
              <Archive className="mr-1.5 h-3.5 w-3.5" /> Arquivar aluno
            </Button>
          )}
        </div>
      </div>

      {loading && <div className="flex items-center gap-2 py-6 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Carregando vínculos…</div>}

      {!loading && (data?.years || []).length === 0 && (
        <p className="py-6 text-center text-sm text-slate-400" data-testid="vinculos-empty">Nenhum vínculo anual registrado.</p>
      )}

      {!loading && (data?.years || []).length > 0 && (
        <>
          <div className="mb-4 flex flex-wrap gap-2" data-testid="vinculos-year-tabs">
            {data.years.map((y) => (
              <button
                key={y.year}
                onClick={() => setYear(y.year)}
                data-testid={`vinculo-year-${y.year}`}
                className={`rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${y.year === year ? 'border-indigo-300 bg-indigo-600 text-white' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}
              >
                {y.year}
                {y.is_active_year && <span className="ml-1.5 text-[10px] font-bold uppercase opacity-80">ativo</span>}
              </button>
            ))}
          </div>

          {current && (
            <motion.div key={current.year} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} data-testid={`vinculo-panel-${current.year}`}>
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold text-slate-700" data-testid="vinculo-class-name">
                  {current.class_name || 'Sem turma'}
                </span>
                <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${STATUS_STYLE[current.status] || 'bg-slate-50 text-slate-600 border-slate-200'}`} data-testid="vinculo-status">
                  {current.status}
                </span>
                <span className="text-[11px] text-slate-400">origem: {current.source}</span>
                {current.is_active_year && <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600"><CheckCircle2 className="h-3.5 w-3.5" /> turma ativa do aluno</span>}
              </div>

              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <Metric icon={GraduationCap} label="Média" value={current.media ?? '—'} sub={`${current.grades_count} notas`} />
                <Metric icon={Activity} label="Faltas" value={current.absences} sub={`${current.attendance_count} registros`} />
                {financeiroVisivel && (
                  <>
                    <Metric icon={Wallet} label="Títulos" value={current.titles_count} sub={`${current.titles_open} abertos`} />
                    <Metric icon={Wallet} label="Valor" value={`R$ ${(current.titles_amount || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`} sub={`ano ${current.year}`} />
                  </>
                )}
              </div>

              <div className="mt-4 flex flex-wrap gap-2 border-t border-dashed border-slate-200 pt-4">
                <Button size="sm" variant="outline" onClick={() => { setAction({ kind: 'transfer', link: current }); setReason(''); setTargetClass(''); }} data-testid="vinculo-transfer-btn">
                  <ArrowRightLeft className="mr-1.5 h-3.5 w-3.5 text-orange-500" /> Transferir turma ({current.year})
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setAction({ kind: 'remove', link: current }); setReason(''); }} data-testid="vinculo-remove-btn">
                  <UserMinus className="mr-1.5 h-3.5 w-3.5 text-amber-600" /> Remover da turma
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setAction({ kind: 'cancel', link: current }); setReason(''); }} data-testid="vinculo-cancel-btn">
                  <XCircle className="mr-1.5 h-3.5 w-3.5 text-rose-600" /> Cancelar matrícula {current.year}
                </Button>
                {!current.is_active_year && current.class_group_id && current.status !== 'cancelado' && (
                  <Button size="sm" variant="outline" disabled={busy} onClick={() => post(`/student/${studentId}/activate-year/${current.year}`)} data-testid="vinculo-activate-btn">
                    <CheckCircle2 className="mr-1.5 h-3.5 w-3.5 text-emerald-600" /> Ativar ano letivo {current.year}
                  </Button>
                )}
              </div>
            </motion.div>
          )}
        </>
      )}

      {data?.dependencies && !data.dependencies.can_hard_delete && (
        <p className="mt-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-800" data-testid="vinculos-delete-block-note">
          <ShieldAlert className="mt-0.5 h-4 w-4 flex-shrink-0" />
          Exclusão definitiva bloqueada: {data.dependencies.grades} notas, {data.dependencies.attendance} presenças e {data.dependencies.titles} títulos vinculados. Use "Arquivar aluno".
        </p>
      )}

      <Dialog open={!!action} onOpenChange={(o) => { if (!o) { setAction(null); setReason(''); } }}>
        <DialogContent data-testid="vinculo-action-dialog">
          <DialogHeader>
            <DialogTitle>
              {action?.kind === 'transfer' && `Transferir turma · ${action?.link?.year}`}
              {action?.kind === 'remove' && `Remover da turma · ${action?.link?.year}`}
              {action?.kind === 'cancel' && `Cancelar matrícula · ${action?.link?.year}`}
              {action?.kind === 'archive' && 'Arquivar aluno'}
            </DialogTitle>
            <DialogDescription>
              {action?.kind === 'remove' && 'O aluno sai da turma desse ano. Notas, faltas e títulos permanecem intactos.'}
              {action?.kind === 'cancel' && 'A matrícula desse ano fica cancelada. Nada é apagado; o histórico continua consultável.'}
              {action?.kind === 'transfer' && 'Troca de turma dentro do mesmo ano letivo. Para outro ano use a Rematrícula.'}
              {action?.kind === 'archive' && 'Arquivamento reversível (soft). Nenhum dado é apagado.'}
            </DialogDescription>
          </DialogHeader>

          {action?.kind === 'transfer' && (
            <select
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              value={targetClass}
              onChange={(e) => setTargetClass(e.target.value)}
              data-testid="vinculo-transfer-class-select"
            >
              <option value="">Selecione a turma destino ({year})</option>
              {sameYearClasses.filter((c) => c.id !== action?.link?.class_group_id).map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          )}

          <Input
            placeholder="Motivo (obrigatório)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="vinculo-reason-input"
          />

          <DialogFooter>
            <Button variant="outline" onClick={() => setAction(null)} data-testid="vinculo-action-cancel">Cancelar</Button>
            <Button disabled={busy || !reason.trim()} onClick={confirmAction} data-testid="vinculo-action-confirm">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirmar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
